<template>
	<footer class="Footer">
		<template v-if="minimal">
			<div class="Footer-minimal">
				<div class="Footer-minimal-overflow">
					<a href="https://birds.cornell.edu"><strong>The Cornell Lab</strong></a>
					<slot name="minimal"></slot>
					<span class="Footer-copyright"> © {{ new Date().getFullYear() }} Cornell University </span>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="Footer-main">
				<div class="Footer-main-logo">
					<a href="https://birds.cornell.edu">
						<img :src="`${logoBaseUrl}clo_horizontal_white_web.svg`" alt="Cornell Lab of Ornithology" />
					</a>
				</div>
				<div class="Footer-main-lists">
					<slot name="main"></slot>
				</div>
				<div class="Footer-main-misc">
					<slot name="misc"></slot>
				</div>
			</div>
			<div class="Footer-cornell" :class="{ 'Footer-cornell--red': red }">
				<div class="Footer-cornell-logo">
					<img :src="`${cornellLogoBaseUrl}cornell_reduced_white_web.svg`" alt="Cornell University" class="u-hideForMedium" />
					<img :src="`${cornellLogoBaseUrl}cornell_logo_simple_white_web.svg`" alt="Cornell University" class="u-showForMedium" />
				</div>
				<span class="Footer-copyright"> © {{ new Date().getFullYear() }} Cornell University </span>
			</div>
		</template>
	</footer>
</template>

<script>
import CloFooter from '@/navigation/CloFooter/CloFooter.vue';
import CloFooterList from '@/navigation/CloFooter/CloFooterList.vue';
import CloFooterListItem from '@/navigation/CloFooter/CloFooterListItem.vue';
import CloFooterListHeading from '@/navigation/CloFooter/CloFooterListHeading.vue';
import CloFooterBadges from '@/navigation/CloFooter/CloFooterBadges.vue';
import CloFooterSocial from '@/navigation/CloFooter/CloFooterSocial.vue';
import CloButton from '@/base/CloButton/CloButton.vue';

export default {
	name: 'CloFooter',
	components: {},
	props: {
		logoBaseUrl: {
			type: String,
			default: 'https://clo-brand-static-prod.s3.amazonaws.com/logos/clo/',
		},
		cornellLogoBaseUrl: {
			type: String,
			default: 'https://clo-brand-static-prod.s3.amazonaws.com/logos/cornell/',
		},
		minimal: {
			type: Boolean,
			default: false,
		},
		red: {
			type: Boolean,
			default: false,
		},
	},
};
</script>

<style lang="scss">
.Footer {
	background-color: $brand-color-warm-black;
	color: $color-white;
	.Footer-main {
		@include inset-responsive;
		@include text(2);
		display: grid;
		@include breakpoint(large) {
			grid-template-columns: 18rem 4fr minmax(6rem, 1fr);
		}
		gap: $space-md $space-lg;
		.Footer-main-logo {
			img {
				height: 2rem;
			}
		}
		.Footer-main-lists {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
			gap: $space-md $space-lg;
		}
		.Footer-main-misc {
			display: flex;
			flex-direction: column;
			gap: $space-sm;
		}
		.Button {
			@include interactiveColor(background, $brand-color-red-shade, $brand-color-red-shade);
			@include stack-sm;
		}
	}
	.Footer-cornell {
		padding: $space-md;
		border-top: 1px solid $color-black;
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		justify-content: space-between;
		column-gap: $space-md;
		row-gap: $space-sm;
		@include text(1);
		@include breakpoint(medium) {
			padding-inline: $space-lg;
			@include text(2);
		}
		.Footer-cornell-logo {
			img {
				height: 3rem;
				@include breakpoint(medium) {
					height: 4.5rem;
				}
			}
		}
		&--red {
			border-top: none;
			background: $brand-color-red-shade;
		}
	}
	.Footer-minimal {
		@include text(1);
		@include inset-sm;
		@include breakpoint(medium) {
			@include text(2);
		}
		.Footer-minimal-overflow {
			overflow-x: auto;
			display: flex;
			align-items: baseline;
			column-gap: $space-md;
			row-gap: $space-xs;
		}
		a {
			flex-shrink: 0;
			@include interactiveColor(color, $color-white, $color-white);
			text-underline-offset: $space-rel-xs;
			text-decoration-line: underline;
			text-decoration-style: solid;
			text-decoration-thickness: 0.0625rem;
			@include interactiveColor(text-decoration-color, transparent, $color-text-reverse);
		}
		.Footer-list {
			flex-shrink: 0;
			flex-grow: 1;
			ul {
				display: flex;
				align-items: baseline;
				li {
					flex-shrink: 0;
					margin: 0;
				}
				li:not(:last-child):after {
					color: subdueColor($color-text-reverse);
					content: '|';
					margin: 0 $space-sm;
				}
			}
		}
		.Footer-copyright {
			flex-shrink: 0;
			@include text(1);
		}
	}
}
</style>
